import React from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import moment from "moment";

export default function SelectBillingPeriod({
  billingPeriods,
  selectedBillingPeriod,
  setSelectedBillingPeriod,
  isCustomDateRange,
  setIsCustomDateRange,
  billingLoading,
}) {
  const { setFieldValue } = useFormikContext();

  const selectCustomDateRange = () => {
    if (isCustomDateRange) {
      setIsCustomDateRange(false);
      return;
    }

    setIsCustomDateRange(true);
    setSelectedBillingPeriod(null);
    setFieldValue("startDate", null);
    setFieldValue("endDate", null);
  };

  // Sort all periods in ASC order (oldest to newest)
  const sortedPeriods = [...billingPeriods].sort((a, b) => 
    moment(a.startDate).valueOf() - moment(b.startDate).valueOf()
  );

  // Find current and previous billing periods
  const today = moment();
  const currentPeriodIndex = sortedPeriods.findIndex(p => 
    today.isSameOrAfter(moment(p.startDate)) && today.isSameOrBefore(moment(p.endDate))
  );

  let recentPeriods = [];
  let morePeriods = [];

  if (currentPeriodIndex !== -1) {
    // Current period found
    const currentPeriod = sortedPeriods[currentPeriodIndex];
    const previousPeriod = currentPeriodIndex > 0 ? sortedPeriods[currentPeriodIndex - 1] : null;

    // Recent section: previous + current (in ASC order)
    if (previousPeriod) {
      recentPeriods.push(previousPeriod);
    }
    recentPeriods.push(currentPeriod);

    // More section: everything else (excluding recent)
    morePeriods = sortedPeriods.filter((p, idx) => 
      idx !== currentPeriodIndex && (previousPeriod ? p.id !== previousPeriod.id : true)
    );
  } else {
    // No current period found, all periods go to "More"
    morePeriods = sortedPeriods;
  }

  const billingPeriodOptions = [
    // Recent Billing Periods section
    ...recentPeriods.map(p => ({
      id: p.id,
      label: `${p.label} · ${moment(p.startDate).format("MMM D")}-${moment(p.endDate).format("D")}`,
      value: p,
      group: "Recent Billing Periods",
    })),
    // More Billing Periods section
    {
      id: "custom", 
      label: "Select Custom Date Range", 
      value: "custom",
      group: "More Billing Periods",
    },
    ...morePeriods.map(p => ({
      id: p.id,
      label: `${p.label} · ${moment(p.startDate).format("MMM D")}-${moment(p.endDate).format("D")}`,
      value: p,
      group: "More Billing Periods",
    })),
  ];

  const billingPeriodValue = isCustomDateRange
    ? { id: "custom", label: "Select Custom Date Range", value: "custom", group: "More Billing Periods" }
    : selectedBillingPeriod
    ? billingPeriodOptions.find(opt => opt.id === selectedBillingPeriod.id) || null
    : null;

  const handleBillingPeriodChange = (event, newValue) => {
    if (newValue?.value === "custom") {
      selectCustomDateRange();
    } else if (newValue?.value) {
      setIsCustomDateRange(false);
      setSelectedBillingPeriod(newValue.value);
      setFieldValue("startDate", moment(newValue.value.startDate));
      setFieldValue("endDate", moment(newValue.value.endDate));
    } else {
      setIsCustomDateRange(false);
      setSelectedBillingPeriod(null);
      setFieldValue("startDate", null);
      setFieldValue("endDate", null);
    }
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>
        Select Billing Period
      </Typography>
      {billingLoading ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <>
          <Autocomplete
            fullWidth
            options={billingPeriodOptions}
            getOptionLabel={option => option.label || ""}
            value={billingPeriodValue}
            onChange={handleBillingPeriodChange}
            groupBy={option => option.group}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  fontWeight: option.value === "custom" ? 500 : "inherit",
                  color: option.value === "custom" ? "primary.main" : "inherit",
                }}
              >
                {option.label}
              </Box>
            )}
            renderInput={params => (
              <TextField {...params} label="Billing Period" variant="outlined" size="small" />
            )}
          />
        </>
      )}
    </Box>
  );
}
